// Types
import type { TErrors, TNewPatch, TPatch } from '~/misc/patch/types';
import type { TFormPatch } from '~/misc/patch/form/types';

// Composables
import { useNotifyResource } from '~/misc/resource/composables/use-notify-resource';

export const usePatchForm = (
  patch: ComputedRef<TFormPatch | null>,
  onUpdate: (patchId: number, payload: TNewPatch) => Promise<TPatch | void>,
  onStore: (payload: TNewPatch) => Promise<TPatch | null>,
  onUpsert: (patch: TPatch) => void
) => {
  const clonePatch = (patch: TFormPatch | null = null): TFormPatch => {
    return JSON.parse(
      JSON.stringify(
        patch || {
          name: '',
          type: 'sale',
          areas: [],
        }
      )
    );
  };

  const payload: Ref<TFormPatch> = ref<TFormPatch>(clonePatch(patch.value));
  const errors: Ref<TErrors> = ref<TErrors>({});
  const isLoading: Ref<boolean> = ref<boolean>(false);

  const setIsLoading = (state: boolean) => {
    isLoading.value = state;
  };

  const { notifyFailure } = useNotifyResource();

  const setupForm = (patch: TFormPatch | null = null) => {
    errors.value = {};
    payload.value = clonePatch(patch);
  };

  const submit = (): Promise<TPatch> => {
    // TODO: change approach due to "[Vue warn]: Unhandled error during execution of native event handler" if no slot passed
    return new Promise<TPatch>((resolve, reject) => {
      setIsLoading(true);

      (payload.value?.id ? onUpdate(payload.value.id, payload.value) : onStore(payload.value))
        .then((response: TPatch | void | null) => {
          const patch: TPatch = response || (payload.value as TPatch);

          onUpsert(patch);
          setupForm();
          resolve(patch);
        })
        .catch((e) => {
          // TODO: encapsulate and reuse
          if (e?.response?.data?.errors) {
            errors.value = e.response.data.errors;
          } else {
            notifyFailure(e?.response?.data?.message || undefined);
          }

          reject(e);
        })
        .finally(() => {
          setIsLoading(false);
        });
    });
  };

  watch(patch, (value: TFormPatch | null) => setupForm(value));

  return { payload, errors, isLoading, submit };
};
